import { Settings } from "@geist-ui/icons";
import { ReactNode } from "react";
import {
  FileTextIcon,
  RocketIcon,
  StackIcon,
  TokensIcon,
  VideoIcon,
} from "@radix-ui/react-icons";
import { LightbulbIcon } from "lucide-react";
import { Route01 } from "@/v2/assets/icons/Route01";
import { Kanban } from "@/v2/assets/icons/Kanban";
import { Home03Icon } from "@/v2/assets/icons/Home03Icon";
import { AiBrowser } from "@/v2/assets/icons/AiBrowser";
import AnalyticsUpIcon from "@/v2/assets/icons/AanalyticsUpIcon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { AiFolderIcon } from "@/v2/assets/icons/AiFolderIcon";
import { AiVisionRecognition } from "@/v2/assets/icons/AiVisionRecognition";
import { Rocket01 } from "@/v2/assets/icons/Rocket01";
import { News } from "@/v2/assets/icons/News";
import { Browser } from "@/v2/assets/icons/Browser";
import { AIsheet } from "@/v2/assets/icons/AIsheet";
import AiProgramming from "@/v2/assets/icons/AiProgramming";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";
import { CheckmarkBadgeIcon } from "@/v2/assets/icons/CheckmarkBadgeIcon";
import type { RadixColor } from "@/v2/assets/icons/Icon.model";
import { NotificationSquareIcon } from "@/v2/assets/icons/NotificationSquare";
import { Files02Icon } from "@/v2/assets/icons/Files02Icon";
import { NoteRemoveIcon } from "@/v2/assets/icons/NoteRemoveIcon";

export enum ENavigate {
  HOME = "home",
  ASSESSMENT = "topics",
  TICKETS = "tickets",
  GETTING_STARTED = "getting-started",
  SETTINGS = "settings",
  KNOWLEDGE = "knowledge-management",
  KNOWLEDGE_EDITOR = "knowledge-management-editor",
  KNOWLEDGE_VIEW = "knowledge-management/articles",
  KNOWLEDGE_GAPS = "knowledge-management/gaps",
  SAMPLING = "sampling",
  VOC = "categories",
  SIMULATOR = "automation",
  SESSIONS_REPORTS = "sessions-reports",
  QUALITY = "quality",
  EVALUATIONS = "quality/evaluations",
  VALIDATIONS = "quality/validations",
  QA_SETTINGS = "quality/scorecards",
  COPILOT = "automation?f=copilot",
  AUTOPILOT = "automation?f=autopilot",
  EXPLORE_INSIGHTS = "explore/insights",
  EXPLORE_TICKETS = "explore/tickets",
  EXPLORE_CONVERSATIONS = "explore/conversations",
  EXPLORE_VIEWS = "explore/views",
  JOURNEYS = "journeys",
  PAGES = "pages",
  COPILOT_FEEDBACK = "copilot-feedback",
  AUTOMATION = "automation-center",
  TRAINING_CENTER = "automation-center/training",
  APOLLO_AGENT = "automation-center/apollo-agent",
  SKILLS = "automation-center/skills",
  ENRICHMENTS = "automation-center/enrichments",
  REGRESSION_SETS = "automation-center/regression-sets",
  REGRESSION_TESTS_V2 = "automation-center/aui-regression-tests/v2",
  TRACKERS = "trackers",
  APPROVAL_REQUESTS = "approval-requests",
  GOOD_RESPONSES_LIBRARY = "automation-center/good-responses-library",
  NOTICES = "notices",
}

// Color mapping for each page when active
export const PageColors: { [key in ENavigate]?: RadixColor } = {
  [ENavigate.HOME]: "blue",
  [ENavigate.EXPLORE_TICKETS]: "blue",
  [ENavigate.EXPLORE_CONVERSATIONS]: "orange",
  [ENavigate.QUALITY]: "purple",
  [ENavigate.TRAINING_CENTER]: "cyan",
  [ENavigate.APOLLO_AGENT]: "violet",
  [ENavigate.SKILLS]: "orange",
  [ENavigate.ENRICHMENTS]: "plum",
  [ENavigate.TRACKERS]: "teal",
  [ENavigate.REGRESSION_SETS]: "sky",
  [ENavigate.REGRESSION_TESTS_V2]: "cyan",
  [ENavigate.JOURNEYS]: "purple",
  [ENavigate.KNOWLEDGE_EDITOR]: "cyan",
  [ENavigate.KNOWLEDGE]: "gray",
  [ENavigate.PAGES]: "indigo",
  [ENavigate.EXPLORE_VIEWS]: "amber",
  [ENavigate.AUTOMATION]: "gray",
  [ENavigate.APPROVAL_REQUESTS]: "green",
  [ENavigate.GOOD_RESPONSES_LIBRARY]: "green",
  [ENavigate.NOTICES]: "orange",
};

// Function to get icon for a page with active state
export const getPageIcon = (
  page: ENavigate,
  active: boolean = false
): ReactNode => {
  const color: "currentColor" | RadixColor =
    active && PageColors[page] ? PageColors[page] : "gray";

  switch (page) {
    case ENavigate.HOME:
      return <Home03Icon fill={color} />;
    case ENavigate.GETTING_STARTED:
      return <>👉</>;
    case ENavigate.SETTINGS:
      return <Settings />;
    case ENavigate.SESSIONS_REPORTS:
      return <VideoIcon />;
    case ENavigate.VOC:
      return <TokensIcon />;
    case ENavigate.ASSESSMENT:
      return <StackIcon />;
    case ENavigate.SAMPLING:
      return undefined;
    case ENavigate.SIMULATOR:
      return <RocketIcon />;
    case ENavigate.KNOWLEDGE:
      return <News fill={color} />;
    case ENavigate.KNOWLEDGE_EDITOR:
      return <AiFolderIcon fill={color} />;
    case ENavigate.QUALITY:
      return <AiVisionRecognition fill={color} />;
    case ENavigate.EVALUATIONS:
      return undefined;
    case ENavigate.VALIDATIONS:
      return undefined;
    case ENavigate.QA_SETTINGS:
      return undefined;
    case ENavigate.AUTOPILOT:
      return undefined;
    case ENavigate.COPILOT:
      return undefined;
    case ENavigate.KNOWLEDGE_GAPS:
      return <NoteRemoveIcon fill={color} />;
    case ENavigate.KNOWLEDGE_VIEW:
      return <Files02Icon fill={color} />;
    case ENavigate.EXPLORE_INSIGHTS:
      return <LightbulbIcon />;
    case ENavigate.EXPLORE_TICKETS:
      return <Ticket03Icon fill={color} />;
    case ENavigate.EXPLORE_CONVERSATIONS:
      return <Comment01Icon fill={color} />;
    case ENavigate.EXPLORE_VIEWS:
      return <AnalyticsUpIcon fill={color} />;
    case ENavigate.JOURNEYS:
      return <Route01 fill={color} />;
    case ENavigate.PAGES:
      return <Browser fill={color} />;
    case ENavigate.COPILOT_FEEDBACK:
      return <>🤖</>;
    case ENavigate.AUTOMATION:
      return <Rocket01 fill={color} />;
    case ENavigate.TRAINING_CENTER:
      return <AiBrowser fill={color} />;
    case ENavigate.APOLLO_AGENT:
      return <Rocket01 fill={color} />;
    case ENavigate.SKILLS:
      return <AiProgramming fill={color} />;
    case ENavigate.ENRICHMENTS:
      return <AIsheet fill={color} />;
    case ENavigate.TRACKERS:
      return <Kanban fill={color} />;
    case ENavigate.REGRESSION_SETS:
      return <StructureCheck fill={color} />;
    case ENavigate.REGRESSION_TESTS_V2:
      return <CheckmarkBadgeIcon fill={color} />;
    case ENavigate.APPROVAL_REQUESTS:
      return <CheckmarkBadgeIcon fill={color} />;
    case ENavigate.GOOD_RESPONSES_LIBRARY:
      return <CheckmarkBadgeIcon fill={color} />;
    case ENavigate.NOTICES:
      return <NotificationSquareIcon fill={color} />;
    default:
      return undefined;
  }
};

// Legacy static mapping for backward compatibility
export const PagesIcons: { [key in ENavigate]?: ReactNode } = {
  [ENavigate.HOME]: <Home03Icon fill="gray" />,
  [ENavigate.GETTING_STARTED]: <>👉</>,
  [ENavigate.SETTINGS]: <Settings />,
  [ENavigate.SESSIONS_REPORTS]: <VideoIcon />,
  [ENavigate.VOC]: <TokensIcon />,
  [ENavigate.ASSESSMENT]: <StackIcon />,
  [ENavigate.SAMPLING]: undefined,
  [ENavigate.SIMULATOR]: <RocketIcon />,
  [ENavigate.KNOWLEDGE]: <News fill="gray" />,
  [ENavigate.KNOWLEDGE_EDITOR]: <AiFolderIcon fill="gray" />,
  [ENavigate.QUALITY]: <AiVisionRecognition fill="gray" />,
  [ENavigate.EVALUATIONS]: undefined,
  [ENavigate.VALIDATIONS]: undefined,
  [ENavigate.QA_SETTINGS]: undefined,
  [ENavigate.AUTOPILOT]: undefined,
  [ENavigate.COPILOT]: undefined,
  [ENavigate.KNOWLEDGE_GAPS]: <NoteRemoveIcon fill="gray" />,
  [ENavigate.KNOWLEDGE_VIEW]: <Files02Icon fill="gray" />,
  [ENavigate.EXPLORE_INSIGHTS]: <LightbulbIcon />,
  [ENavigate.EXPLORE_TICKETS]: <Ticket03Icon fill="gray" />,
  [ENavigate.EXPLORE_CONVERSATIONS]: <Comment01Icon fill="gray" />,
  [ENavigate.EXPLORE_VIEWS]: <AnalyticsUpIcon fill="gray" />,
  [ENavigate.JOURNEYS]: <Route01 fill="gray" />,
  [ENavigate.PAGES]: <Browser fill="gray" />,
  [ENavigate.COPILOT_FEEDBACK]: <>🤖</>,
  [ENavigate.AUTOMATION]: <Rocket01 fill="gray" />,
  [ENavigate.TRAINING_CENTER]: <AiBrowser fill="gray" />,
  [ENavigate.APOLLO_AGENT]: <Rocket01 fill="gray" />,
  [ENavigate.SKILLS]: <AiProgramming fill="gray" />,
  [ENavigate.ENRICHMENTS]: <AIsheet fill="gray" />,
  [ENavigate.TRACKERS]: <Kanban fill="gray" />,
  [ENavigate.REGRESSION_SETS]: <StructureCheck fill="gray" />,
  [ENavigate.REGRESSION_TESTS_V2]: <CheckmarkBadgeIcon fill="gray" />,
  [ENavigate.APPROVAL_REQUESTS]: <CheckmarkBadgeIcon fill="gray" />,
  [ENavigate.NOTICES]: <NotificationSquareIcon fill="gray" />,
};

const tooltipMapper: { [x: string]: string } = {
  [ENavigate.HOME]: "Dashboard",
  [ENavigate.ASSESSMENT]: "Topics",
  [ENavigate.GETTING_STARTED]: "Getting Started",
  [ENavigate.SIMULATOR]: "Automation",
  [ENavigate.KNOWLEDGE]: "Knowledge management",
  [ENavigate.VOC]: "Categories",
  [ENavigate.QUALITY]: "Quality",
  [ENavigate.EVALUATIONS]: "Evaluations",
  [ENavigate.VALIDATIONS]: "Validations",
  [ENavigate.SAMPLING]: "Sampling",
  [ENavigate.COPILOT]: "Copilot",
  [ENavigate.AUTOPILOT]: "Auto-pilot",
  [ENavigate.EXPLORE_INSIGHTS]: "Insights",
  [ENavigate.EXPLORE_TICKETS]: "Tickets",
  [ENavigate.EXPLORE_CONVERSATIONS]: "Conversations",
  [ENavigate.EXPLORE_VIEWS]: "Views",
  [ENavigate.KNOWLEDGE_VIEW]: "Articles",
  [ENavigate.KNOWLEDGE_GAPS]: "Knowledge gaps",
  [ENavigate.COPILOT_FEEDBACK]: "Copilot Feedback",
  [ENavigate.SKILLS]: "Skills",
  [ENavigate.ENRICHMENTS]: "Enrichments",
  [ENavigate.REGRESSION_SETS]: "Regression Sets",
  [ENavigate.REGRESSION_TESTS_V2]: "Simulation Tests",
  [ENavigate.TRACKERS]: "Trackers",
  [ENavigate.TRAINING_CENTER]: "Training Center",
  [ENavigate.APOLLO_AGENT]: "Apollo Agent",
  [ENavigate.AUTOMATION]: "Automation",
  [ENavigate.APPROVAL_REQUESTS]: "Approval Requests",
  [ENavigate.GOOD_RESPONSES_LIBRARY]: "Good Feedback Library",
};
